import React, { useEffect, useRef, useState } from 'react';
import { Phone, X, Loader2 } from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const EMPTY = { name: '', phone: '', subject: '', note: '' };

// Türkiye cep numarası: 5xx xxx xx xx (başında 0 ya da 90 olabilir).
export const normalizePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('90')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
};
export const isValidPhone = (value) => /^5\d{9}$/.test(normalizePhone(value));

const detailText = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (error?.response?.status === 429) return 'Kısa süre içinde çok fazla talep gönderdiniz. Lütfen daha sonra tekrar deneyin.';
  return 'Talebiniz gönderilemedi. Lütfen bilgileri kontrol edip tekrar deneyin.';
};

const CallbackModal = ({ open, onClose }) => {
  const { user } = useAuth() || {};
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sending = useRef(false);
  const { toast } = useToast();

  // Giriş yapmış üyenin adı ve telefonu önceden doldurulur.
  useEffect(() => {
    if (!open) return;
    setError('');
    setForm((current) => ({
      ...current,
      name: current.name || [user?.first_name, user?.last_name].filter(Boolean).join(' '),
      phone: current.phone || user?.phone || '',
    }));
  }, [open, user]);

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (sending.current) return; // çift gönderim
    const name = form.name.trim();
    if (name.length < 2) { setError('Ad soyad girin.'); return; }
    if (!isValidPhone(form.phone)) { setError('Geçerli bir cep telefonu girin (5xx xxx xx xx).'); return; }
    setError('');
    sending.current = true;
    setBusy(true);
    try {
      await client.post('/callback', { ...form, name, phone: normalizePhone(form.phone) });
      toast({ title: 'Talebiniz alındı', description: 'Kısa süre içinde sizi arayacağız' });
      setForm(EMPTY);
      onClose();
    } catch (err) {
      setError(detailText(err));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-bhs="callback-modal">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} />
      <div className="responsive-dialog relative w-full max-w-md pusu-panel overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="callback-title">
        <button type="button" onClick={onClose} aria-label="Kapat" className="absolute top-4 right-4 side-icon-btn inline-flex" style={{ width: 36, height: 36 }}><X size={16} /></button>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Phone size={18} className="text-[var(--pusu-gold)]" />
            <h2 id="callback-title" className="text-xl font-black gold-text">Beni Ara</h2>
          </div>
          <p className="text-sm text-gray-400 mb-5">Adınızı ve telefonunuzu bırakın, sizi arayalım.</p>
          <form onSubmit={submit} className="space-y-3" noValidate>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ad Soyad" className="pusu-input" autoComplete="name" maxLength={128} />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefon (5xx xxx xx xx)" className="pusu-input" inputMode="tel" autoComplete="tel" maxLength={20} />
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Konu (opsiyonel)" className="pusu-input" maxLength={255} />
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Mesajınız (opsiyonel)" className="pusu-input" style={{ minHeight: 80, padding: 12 }} maxLength={4000} />
            {error && <p className="text-sm text-red-400" role="alert" data-testid="callback-error">{error}</p>}
            <button type="submit" disabled={busy} className="gold-btn w-full py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">{busy && <Loader2 size={14} className="animate-spin" />} Talebi Gönder</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CallbackModal;
